import React, { useEffect, useState } from "react";
import { AppBar, Box } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useLocation } from "react-router-dom";

// Matches the account row at the foot of the navigation drawer so the two line
// up on the pinned layout: a 32px avatar with 10px of padding above and below.
//
// That row measures 53px, but the extra pixel is its own top divider and this
// bar has none. Matching 53 would put the bar's top edge level with the top of
// that divider; 52 puts it level with where the row's content starts, which is
// the edge the eye follows across.
const BAR_HEIGHT = 52;
const LOGO_HEIGHT = 34;

export default function ToolbarWithLogo() {
    const theme = useTheme();
    const location = useLocation();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [mobileVisibility, setMobileVisibility] = useState(1);

    useEffect(() => {
        const isMobileHomepage = isMobile && location.pathname === "/";

        if (!isMobileHomepage) {
            setMobileVisibility(1);
            return undefined;
        }

        const fadeDistance = 120;

        const updateVisibility = () => {
            const scrollTop =
                window.scrollY || document.documentElement.scrollTop || 0;
            const nextVisibility = Math.max(
                0,
                Math.min(1, 1 - scrollTop / fadeDistance),
            );

            setMobileVisibility(nextVisibility);
        };

        updateVisibility();
        window.addEventListener("scroll", updateVisibility, { passive: true });

        return () => window.removeEventListener("scroll", updateVisibility);
    }, [isMobile, location.pathname]);

    if (isMobile && location.pathname !== "/") {
        return null;
    }

    return (
        <>
            {/* Fixed bottom AppBar */}
            <AppBar
                position="fixed"
                color="default"
                sx={{
                    top: "auto",
                    bottom: 0,
                    color: "text.primary",
                    backgroundColor: "background.paper",
                    borderTop: "1px solid",
                    borderColor: "divider",
                    boxShadow: (theme) =>
                        theme.palette.mode === "dark"
                            ? "0 -1px 8px rgba(0, 0, 0, 0.28)"
                            : "0 -1px 4px rgba(0, 0, 0, 0.1)",
                    height: BAR_HEIGHT,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    opacity: isMobile ? mobileVisibility : 1,
                    pointerEvents:
                        isMobile && mobileVisibility === 0 ? "none" : "auto",
                    transition: "opacity 80ms linear",
                    "@media (prefers-reduced-motion: reduce)": {
                        transition: "none",
                    },
                }}
            >
                {/* Logo centered inside */}
                <Box
                    component="img"
                    src="/images/FAW_CLEAR.png"
                    alt="FAW Logo"
                    sx={{
                        height: LOGO_HEIGHT,
                        userSelect: "none",
                    }}
                />
            </AppBar>

            {/* Spacer div to push page content above the fixed toolbar */}
            <div
                aria-hidden="true"
                style={{
                    height: isMobile
                        ? BAR_HEIGHT * mobileVisibility
                        : BAR_HEIGHT,
                    transition: "height 80ms linear",
                }}
            />
        </>
    );
}
